/**
 * What can this printing actually be, decided from rows alone.
 *
 * The pure half of variantOptions, kept in its own file because it has no
 * business needing a database connection. Importing it used to drag in the
 * Supabase client, which reads credentials at import time, so a build script
 * that only wanted to reduce some rows could not load it at all.
 *
 * Both callers now share this one copy: the app, which fetches rows and
 * reduces them, and the script that bakes the offline snapshot. What a phone
 * offers with no signal and what it offers on a connection therefore cannot
 * drift apart, which was the whole risk of writing this logic twice.
 */

/** Best to worst, so a picker lists conditions in the order a collector reads them. */
const TYPE_ORDER = ['normal', 'reverse', 'holo', 'metal', 'lenticular'];
const orderBy = (list, order) =>
  list.slice().sort((a, b) => {
    const ia = order.indexOf(a), ib = order.indexOf(b);
    return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib) || a.localeCompare(b);
  });


/** Pure half of getVariantOptions, so it can be tested without a database. */
export function reduceVariants(rows) {
  const types = new Set();
  const subtypes = new Set();
  const stamps = new Set();
  const foilsByType = {};
  let counted = 0;

  for (const row of rows || []) {
    const carriesAxis = Boolean(row.subtype) || Boolean(row.foil)
      || (row.stamps && row.stamps.length > 0)
      || (row.type && row.type !== 'normal');
    if (!carriesAxis) continue;
    counted++;

    if (row.type) types.add(row.type);
    if (row.subtype) subtypes.add(row.subtype);
    (row.stamps || []).forEach((s) => stamps.add(s));
    if (row.foil && row.type) {
      (foilsByType[row.type] = foilsByType[row.type] || new Set()).add(row.foil);
    }
  }

  // A card with any recorded printing can also be the plain one, unless every
  // printing says otherwise — Base Set Charizard was never sold non-holo.
  const hasPlain = (rows || []).some(
    (r) => r.type === 'normal' && !r.subtype && !r.foil && !(r.stamps || []).length
  );
  if (hasPlain && counted > 0 && !types.has('normal')) {
    // Only trust a plain row when this card has no evidence of being holo-only.
    const holoOnly = types.has('holo') && !types.has('reverse');
    if (!holoOnly) types.add('normal');
  }

  const out = {
    types: orderBy([...types], TYPE_ORDER),
    foilsByType: Object.fromEntries(
      Object.entries(foilsByType).map(([t, set]) => [t, [...set].sort()])
    ),
    subtypes: [...subtypes].sort(),
    stamps: [...stamps].sort(),
    count: counted,
  };

  // Nothing recorded beyond plain printings: the card has one form.
  if (out.types.length === 0) out.types = ['normal'];
  return out;
}
